import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { 
  Upload, FileJson, FileSpreadsheet, Search, Plus, Trash2, Edit, 
  ChefHat, Loader2, CheckCircle, AlertCircle, Download, Filter,
  Utensils, Clock, Users, Database
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const RecipesAdmin = () => {
  const { API, user } = useAuth();
  const fileInputRef = useRef(null);
  
  // States
  const [recipes, setRecipes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Pagination & filters
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [mealTypeFilter, setMealTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  
  // Edit modal
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [newRecipe, setNewRecipe] = useState({
    name: '',
    ingredients: '',
    instructions: '',
    meal_type: 'lunch',
    category: 'general',
    prep_time: '',
    source: ''
  });

  useEffect(() => {
    fetchRecipes();
    fetchStats();
  }, [page, search, mealTypeFilter, categoryFilter]);

  const fetchRecipes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      if (mealTypeFilter) params.append('meal_type', mealTypeFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      
      const response = await axios.get(`${API}/recipes?${params}`);
      setRecipes(response.data.recipes || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/recipes/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['application/json', 'text/csv'];
    const ext = file.name.split('.').pop().toLowerCase();
    
    if (!['json', 'csv'].includes(ext)) {
      toast.error('Format nesuportat. Folosește JSON sau CSV.');
      return;
    }

    setUploading(true);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/recipes/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      toast.success(`Import finalizat: ${response.data.imported} rețete importate`);
      fetchRecipes();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la import');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCreateRecipe = async () => {
    if (!newRecipe.name || !newRecipe.ingredients) {
      toast.error('Completează numele și ingredientele');
      return;
    }

    try {
      const recipeData = {
        ...newRecipe,
        ingredients: newRecipe.ingredients.split('\n').map(i => i.trim()).filter(Boolean),
        prep_time: newRecipe.prep_time ? parseInt(newRecipe.prep_time) : null
      };
      
      await axios.post(`${API}/recipes`, recipeData);
      toast.success('Rețetă adăugată cu succes!');
      setNewRecipe({
        name: '',
        ingredients: '',
        instructions: '',
        meal_type: 'lunch',
        category: 'general',
        prep_time: '',
        source: ''
      });
      fetchRecipes();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la adăugare');
    }
  };

  const handleDeleteRecipe = async (recipeId) => {
    if (!window.confirm('Sigur vrei să ștergi această rețetă?')) return;
    
    try {
      await axios.delete(`${API}/recipes/${recipeId}`);
      toast.success('Rețetă ștearsă');
      fetchRecipes();
      fetchStats();
    } catch (error) {
      toast.error('Eroare la ștergere');
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.post(`${API}/recipes/export?format=${format}`);
      
      // Download as file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `retete_nutrihealth.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Export finalizat!');
    } catch (error) {
      toast.error('Eroare la export');
    }
  };

  // Check if user is admin
  if (user?.role !== 'admin' && user?.role !== 'nutritionist') {
    return (
      <div className="min-h-screen bg-[#F5F5F0]">
        <Navbar />
        <div className="container mx-auto px-4 py-12 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-serif text-[#1A4D2E]">Acces restricționat</h1>
          <p className="text-[#4a5568]">Doar administratorii pot accesa această pagină.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-8 max-w-7xl">
        <div className="mb-6 md:mb-8">
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-[#1A4D2E] mb-2">
            Administrare Rețete
          </h1>
          <p className="text-[#4a5568] text-base md:text-lg">
            Importă, gestionează și organizează baza de date cu rețete
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="border-none card-shadow">
              <CardContent className="p-4 text-center">
                <Database className="w-8 h-8 text-[#1A4D2E] mx-auto mb-2" />
                <p className="text-3xl font-bold text-[#1A4D2E]">{stats.total}</p>
                <p className="text-sm text-[#4a5568]">Total rețete</p>
              </CardContent>
            </Card>
            <Card className="border-none card-shadow">
              <CardContent className="p-4 text-center">
                <Utensils className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-3xl font-bold text-amber-600">{stats.by_meal_type?.breakfast || 0}</p>
                <p className="text-sm text-[#4a5568]">Mic dejun</p>
              </CardContent>
            </Card>
            <Card className="border-none card-shadow">
              <CardContent className="p-4 text-center">
                <ChefHat className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-3xl font-bold text-green-600">{stats.by_meal_type?.lunch || 0}</p>
                <p className="text-sm text-[#4a5568]">Prânz</p>
              </CardContent>
            </Card>
            <Card className="border-none card-shadow">
              <CardContent className="p-4 text-center">
                <Clock className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                <p className="text-3xl font-bold text-purple-600">{stats.by_meal_type?.dinner || 0}</p>
                <p className="text-sm text-[#4a5568]">Cină</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="import" className="space-y-4 sm:space-y-6">
          <TabsList className="bg-white p-1 rounded-xl flex flex-wrap gap-1 h-auto">
            <TabsTrigger value="import" className="rounded-lg data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white">
              <Upload className="w-4 h-4 mr-2" />
              Import
            </TabsTrigger>
            <TabsTrigger value="browse" className="rounded-lg data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white">
              <Search className="w-4 h-4 mr-2" />
              Rețete
            </TabsTrigger>
            <TabsTrigger value="add" className="rounded-lg data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white">
              <Plus className="w-4 h-4 mr-2" />
              Adaugă
            </TabsTrigger>
          </TabsList>

          {/* Import Tab */}
          <TabsContent value="import">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Upload Card */}
              <Card className="border-none card-shadow">
                <CardHeader>
                  <CardTitle className="font-serif text-xl text-[#1A4D2E] flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Import Rețete
                  </CardTitle>
                  <CardDescription>
                    Încarcă un fișier JSON sau CSV cu rețetele tale
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div
                    className="border-2 border-dashed border-[#1A4D2E]/30 rounded-xl p-8 text-center hover:border-[#1A4D2E] transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".json,.csv"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    {uploading ? (
                      <Loader2 className="w-12 h-12 text-[#1A4D2E] mx-auto mb-4 animate-spin" />
                    ) : (
                      <Upload className="w-12 h-12 text-[#1A4D2E]/50 mx-auto mb-4" />
                    )}
                    <p className="text-[#1A4D2E] font-medium">
                      {uploading ? 'Se importă...' : 'Click sau trage fișierul aici'}
                    </p>
                    <p className="text-sm text-[#4a5568] mt-2">
                      Suportă JSON și CSV
                    </p>
                  </div>

                  {/* Import Result */}
                  {importResult && (
                    <div className={`p-4 rounded-xl ${importResult.failed > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {importResult.failed > 0 ? (
                          <AlertCircle className="w-5 h-5 text-amber-500" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        )}
                        <span className="font-medium">
                          {importResult.imported} importate, {importResult.failed} eșuate
                        </span>
                      </div>
                      {importResult.errors?.length > 0 && (
                        <ul className="text-sm text-amber-700 mt-2 space-y-1">
                          {importResult.errors.slice(0, 5).map((err, i) => (
                            <li key={i}>• {err}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Format Guide */}
              <Card className="border-none card-shadow">
                <CardHeader>
                  <CardTitle className="font-serif text-xl text-[#1A4D2E] flex items-center gap-2">
                    <FileJson className="w-5 h-5" />
                    Format Fișier
                  </CardTitle>
                  <CardDescription>
                    Cum să pregătești fișierul pentru import
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* JSON Example */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FileJson className="w-4 h-4 text-blue-500" />
                      <span className="font-medium text-[#1A4D2E]">Format JSON</span>
                    </div>
                    <pre className="bg-[#1A4D2E] text-green-300 p-4 rounded-xl text-xs overflow-x-auto">
{`[
  {
    "name": "Ciorbă de burtă",
    "ingredients": ["burtă", "smântână", "usturoi"],
    "instructions": "Se fierbe...",
    "meal_type": "lunch",
    "category": "traditional"
  }
]`}
                    </pre>
                  </div>

                  {/* CSV Example */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FileSpreadsheet className="w-4 h-4 text-green-500" />
                      <span className="font-medium text-[#1A4D2E]">Format CSV</span>
                    </div>
                    <pre className="bg-[#1A4D2E] text-green-300 p-4 rounded-xl text-xs overflow-x-auto">
{`name,ingredients,instructions,meal_type,category
Ciorbă de burtă,burtă|smântână|usturoi,Se fierbe...,lunch,traditional`}
                    </pre>
                    <p className="text-xs text-[#4a5568] mt-2">
                      * Ingredientele separate cu | (pipe)
                    </p>
                  </div>

                  {/* Meal Types */}
                  <div className="pt-4 border-t">
                    <p className="text-sm font-medium text-[#1A4D2E] mb-2">Tipuri de masă:</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge className="bg-amber-100 text-amber-700">breakfast</Badge>
                      <Badge className="bg-green-100 text-green-700">lunch</Badge>
                      <Badge className="bg-purple-100 text-purple-700">dinner</Badge>
                      <Badge className="bg-blue-100 text-blue-700">snack</Badge>
                    </div>
                  </div>

                  {/* Export Button */}
                  <div className="pt-4 border-t">
                    <p className="text-sm font-medium text-[#1A4D2E] mb-2">Export rețete existente:</p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExport('json')}
                        className="rounded-full"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        JSON
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExport('csv')}
                        className="rounded-full"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        CSV
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Browse Tab */}
          <TabsContent value="browse">
            <Card className="border-none card-shadow">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle className="font-serif text-xl text-[#1A4D2E]">
                    Toate Rețetele ({stats?.total || 0})
                  </CardTitle>
                  <div className="flex flex-wrap gap-2">
                    <Input
                      placeholder="Caută..."
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                      className="w-40 h-9 rounded-full bg-[#F5F5F0]"
                    />
                    <select
                      value={mealTypeFilter}
                      onChange={(e) => { setMealTypeFilter(e.target.value); setPage(1); }}
                      className="h-9 px-3 rounded-full bg-[#F5F5F0] border-none text-sm"
                    >
                      <option value="">Toate mesele</option>
                      <option value="breakfast">Mic dejun</option>
                      <option value="lunch">Prânz</option>
                      <option value="dinner">Cină</option>
                      <option value="snack">Gustare</option>
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E] mx-auto" />
                  </div>
                ) : recipes.length > 0 ? (
                  <>
                    <div className="space-y-3">
                      {recipes.map((recipe) => (
                        <div
                          key={recipe.id}
                          className="flex items-center justify-between p-4 bg-[#F5F5F0] rounded-xl hover:bg-[#1A4D2E]/5 transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-[#1A4D2E] truncate">{recipe.name}</h3>
                              <Badge className={`text-xs ${
                                recipe.meal_type === 'breakfast' ? 'bg-amber-100 text-amber-700' :
                                recipe.meal_type === 'lunch' ? 'bg-green-100 text-green-700' :
                                recipe.meal_type === 'dinner' ? 'bg-purple-100 text-purple-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {recipe.meal_type}
                              </Badge>
                            </div>
                            <p className="text-sm text-[#4a5568] truncate">
                              {recipe.ingredients?.slice(0, 5).join(', ')}
                              {recipe.ingredients?.length > 5 && '...'}
                            </p>
                            <div className="flex gap-2 mt-1">
                              <span className="text-xs text-[#4a5568]">{recipe.calories || '?'} kcal</span>
                              {recipe.suitable_for?.map((s, i) => (
                                <Badge key={i} className="text-xs bg-green-100 text-green-700">{s}</Badge>
                              ))}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteRecipe(recipe.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-center gap-2 mt-6">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="rounded-full"
                      >
                        Înapoi
                      </Button>
                      <span className="text-sm text-[#4a5568]">
                        Pagina {page} din {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="rounded-full"
                      >
                        Înainte
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <ChefHat className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                    <p className="text-[#4a5568]">Nu există rețete. Importă sau adaugă rețete noi.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Add Tab */}
          <TabsContent value="add">
            <Card className="border-none card-shadow max-w-2xl">
              <CardHeader>
                <CardTitle className="font-serif text-xl text-[#1A4D2E] flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Adaugă Rețetă Manual
                </CardTitle>
                <CardDescription>
                  Completează detaliile pentru o rețetă nouă
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nume rețetă *</Label>
                    <Input
                      value={newRecipe.name}
                      onChange={(e) => setNewRecipe({ ...newRecipe, name: e.target.value })}
                      placeholder="Ciorbă de legume"
                      className="bg-[#F5F5F0] border-none rounded-lg"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tip masă</Label>
                    <select
                      value={newRecipe.meal_type}
                      onChange={(e) => setNewRecipe({ ...newRecipe, meal_type: e.target.value })}
                      className="w-full h-10 px-3 rounded-lg bg-[#F5F5F0] border-none"
                    >
                      <option value="breakfast">Mic dejun</option>
                      <option value="lunch">Prânz</option>
                      <option value="dinner">Cină</option>
                      <option value="snack">Gustare</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Ingrediente * (câte unul pe linie)</Label>
                  <Textarea
                    value={newRecipe.ingredients}
                    onChange={(e) => setNewRecipe({ ...newRecipe, ingredients: e.target.value })}
                    placeholder="morcovi&#10;ceapă&#10;cartofi&#10;ulei de măsline"
                    className="bg-[#F5F5F0] border-none rounded-lg min-h-[120px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Instrucțiuni</Label>
                  <Textarea
                    value={newRecipe.instructions}
                    onChange={(e) => setNewRecipe({ ...newRecipe, instructions: e.target.value })}
                    placeholder="Pașii de preparare..."
                    className="bg-[#F5F5F0] border-none rounded-lg min-h-[100px]"
                  />
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Categorie</Label>
                    <select
                      value={newRecipe.category}
                      onChange={(e) => setNewRecipe({ ...newRecipe, category: e.target.value })}
                      className="w-full h-10 px-3 rounded-lg bg-[#F5F5F0] border-none"
                    >
                      <option value="general">General</option>
                      <option value="traditional">Tradițional</option>
                      <option value="vegetarian">Vegetarian</option>
                      <option value="vegan">Vegan</option>
                      <option value="low_carb">Low Carb</option>
                      <option value="gluten_free">Fără Gluten</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Timp preparare (min)</Label>
                    <Input
                      type="number"
                      value={newRecipe.prep_time}
                      onChange={(e) => setNewRecipe({ ...newRecipe, prep_time: e.target.value })}
                      placeholder="30"
                      className="bg-[#F5F5F0] border-none rounded-lg"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sursă (carte/pagină)</Label>
                    <Input
                      value={newRecipe.source}
                      onChange={(e) => setNewRecipe({ ...newRecipe, source: e.target.value })}
                      placeholder="Carte, pg. 45"
                      className="bg-[#F5F5F0] border-none rounded-lg"
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <Button
                    onClick={handleCreateRecipe}
                    className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adaugă Rețeta
                  </Button>
                </div>

                <div className="p-4 bg-blue-50 rounded-xl mt-4">
                  <p className="text-sm text-blue-700">
                    <strong>💡 Notă:</strong> Valorile nutriționale (calorii, proteine, etc.) 
                    și clasificarea pentru condiții medicale sunt calculate automat din ingrediente.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default RecipesAdmin;
